import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { UnifiedCalendarDashboard } from '@/components/calendar/UnifiedCalendarDashboard'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface Child {
  id: string
  username: string
  display_name: string
  color_theme?: string
}

/**
 * Calendar page - Shows unified calendar with all event types
 * Route: /dashboard/calendar
 *
 * For parents: Shows all children, all assignments, can add events
 * For kids: Shows themselves, their assignments, and custody/presence info
 */
export const CalendarPage: React.FC = () => {
  const { user } = useAuth()
  const [children, setChildren] = useState<Child[]>([])
  const [assignments, setAssignments] = useState<any[]>([])

  useEffect(() => {
    if (user) {
      loadData()
    }
  }, [user])

  const loadData = async () => {
    try {
      if (user?.is_parent) {
        // Parents see all children and all assignments
        const usersRes = await apiClient.get('/api/users')
        const allUsers = usersRes.users || []
        const childUsers = allUsers.filter((u: any) => !u.is_parent)
        setChildren(childUsers.map((u: any) => ({
          id: u.id,
          username: u.username,
          display_name: u.display_name,
          color_theme: u.color_theme
        })))

        // Load all assignments
        const assignmentsRes = await apiClient.get('/api/assignments')
        let assignmentsData = []
        if (Array.isArray(assignmentsRes)) {
          assignmentsData = assignmentsRes
        } else if (assignmentsRes.assignments) {
          assignmentsData = assignmentsRes.assignments
        } else if (assignmentsRes.data) {
          assignmentsData = assignmentsRes.data
        }
        setAssignments(assignmentsData)
      } else {
        // Kids see themselves and all children for custody display
        const usersRes = await apiClient.get('/api/users')
        const allUsers = usersRes.users || []
        const childUsers = allUsers.filter((u: any) => !u.is_parent)
        setChildren(childUsers.map((u: any) => ({
          id: u.id,
          username: u.username,
          display_name: u.display_name,
          color_theme: u.color_theme
        })))

        // Load all assignments so calendar shows full family status
        const assignmentsRes = await apiClient.get('/api/assignments')
        let assignmentsData = []
        if (Array.isArray(assignmentsRes)) {
          assignmentsData = assignmentsRes
        } else if (assignmentsRes.assignments) {
          assignmentsData = assignmentsRes.assignments
        } else if (assignmentsRes.data) {
          assignmentsData = assignmentsRes.data
        }
        setAssignments(assignmentsData)
      }
    } catch (error) {
      console.error('Failed to load calendar data:', error)
      setChildren([])
      setAssignments([])
    }
  }

  const handleReassignChore = async (assignmentId: string, newChildId: string) => {
    try {
      const assignment = assignments.find((a: any) => a.id === assignmentId)
      const newChild = children.find(c => c.id === newChildId)
      if (!assignment || !newChild) {
        toast.error('Invalid assignment or child')
        return
      }
      await apiClient.put(`/api/assignments/${assignmentId}`, { assigned_to: newChildId })
      toast.success(`Chore reassigned to ${newChild.display_name}`)
      loadData()
    } catch (error) {
      toast.error('Failed to reassign chore')
    }
  }

  return (
    <>
      <UnifiedCalendarDashboard
        assignments={assignments}
        children={children}
        onReassignChore={handleReassignChore}
        onEventCreated={loadData}
      />
    </>
  )
}
